import { useTranslation } from "react-i18next";
import { DashboardCard } from "@/components/common/DashboardCard";
import { Spinner } from "@/components/ui/v2/spinner";
import { useGetTimeAttendanceReportQuery } from "@/services/(my-space)/api.time-attendance";
import { CalendarDays, TimerReset, type LucideIcon } from "lucide-react";
import { useServerToday } from "@/hooks/useServerToday";

export const TimeSummery = ({ className }: { className?: string }) => {
  const { t } = useTranslation("my-space");
  const [year, month] = useServerToday().split("-").map(Number);
  const { data: report, isLoading } = useGetTimeAttendanceReportQuery({
    month,
    year,
  });

  return (
    <DashboardCard
      icon="/icons/interface-icons/sidebar/icon-3d-time-attendance.png"
      title={t("dashboard.time_summary")}
      rootClassname={className}
      scrollable={false}
      action={{
        label: t("dashboard.view_summary"),
        link: "/time-attendance",
      }}
    >
      <div className="flex flex-wrap gap-4 min-h-20">
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <TimeSummaryInfoCard
              color="red"
              icon={CalendarDays}
              dataLabel={t("dashboard.worked_days")}
              title={t("dashboard.days_summary")}
              dataValue2={report?.data?.workedHours?.totalWorkingDaysInMonth ?? 0}
              dataValue1={report?.data?.monthStats?.workedDays ?? 0}
            />
            <TimeSummaryInfoCard
              icon={TimerReset}
              dataLabel={t("dashboard.covered_hours")}
              title={t("dashboard.time_summary_br")}
              dataValue1={report?.data?.workedHours?.formattedWorkedHours ?? ""}
              dataValue2={report?.data?.workedHours?.plannedHours ? report?.data?.workedHours?.plannedHours + "h" : ""}
            />
          </>
        )}
      </div>
    </DashboardCard>
  );
};

const TimeSummaryInfoCard = ({
  title,
  icon: Icon,
  color = "orange",
  dataLabel,
  dataValue1 = "00",
  dataValue2 = "00",
}: {
  title: string;
  color?: string; // Any tailwind supported color only, e.g. red, orange, purple etc.
  icon: LucideIcon;
  dataLabel: string;
  dataValue1: string | number;
  dataValue2: string | number;
}) => {
  const TitleIcon = (
    <div className={`bg-white p-2 rounded-full aspect-square text-${color}-500`}>
      <Icon size={22} />
    </div>
  );

  return (
    <div
      className={`min-w-44 relative overflow-clip flex-1 rounded p-2.5 min-h-44 flex flex-col justify-between bg-${color}-500/10`}
    >
      <div className="flex items-center gap-2">
        {TitleIcon}
        <small className="font-semibold leading-none" dangerouslySetInnerHTML={{ __html: title }} />
      </div>
      <div className="bg-white rounded-xs px-2 py-1 flex flex-col -space-y-0.5">
        <div className="flex gap-1">
          <span className="font-bold">{dataValue1}</span>
          <span className="text-black/50 font-semibold">/</span>
          <span className="text-black/50 font-semibold">{dataValue2}</span>
        </div>
        <small className="text-black/50 font-semibold text-xs">{dataLabel}</small>
      </div>
      <Icon className={`absolute -end-4 top-0 text-${color}-500 rtl-flip`} size={82} opacity={0.5} strokeWidth={1} />
    </div>
  );
};
